import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Camera, MapPin, Clock, Package, Truck, CheckCircle, AlertTriangle, Upload, Loader2, MessageSquare, Star, Flag, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepIcons = {
  accepted: CheckCircle, deposit_received: CheckCircle, started: Package,
  halfway: Clock, completed: Check, shipped: Truck, delivered: CheckCircle,
  en_route: Navigation, arrived: MapPin, in_progress: Clock,
  customer_confirmed: CheckCircle,
};

// ── Milestone Timeline (shared by vendor and customer) ──
export function MilestoneTimeline({ milestones, currentStep, isVendor, bookingId, onUpdate, trackingNumber, progressPercent, progressLabel, revisionsUsed = 0, revisionsLimit = 3, revisionsRemaining }) {
  const [completing, setCompleting] = useState(null);
  const [note, setNote] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [carrierInput, setCarrierInput] = useState('');
  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionText, setRevisionText] = useState('');
  const [sendingRevision, setSendingRevision] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fallback for older callers that don't pass progress info
  const computedPercent = typeof progressPercent === 'number'
    ? progressPercent
    : Math.round(((milestones || []).filter((m) => m.status === 'completed').length / Math.max(1, milestones?.length || 1)) * 100);
  const computedLabel = progressLabel || (
    computedPercent === 0 ? 'Not started'
    : computedPercent === 100 ? 'Completed'
    : `${computedPercent}% — in progress`
  );
  const remaining = typeof revisionsRemaining === 'number' ? revisionsRemaining : Math.max(0, revisionsLimit - revisionsUsed);
  const orderCompleted = computedPercent === 100 || milestones?.some((m) => m.step === 'completed' && m.status === 'completed');

  const requestRevision = async () => {
    if (!revisionText.trim() || revisionText.trim().length < 5) {
      toast.error('Please describe what you would like revised (min 5 chars)');
      return;
    }
    setSendingRevision(true);
    try {
      const res = await fetch(`${API}/milestones/request-revision/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ booking_id: bookingId, reason: revisionText.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.exceeded_free_limit) {
          toast.warning('Revision sent. You\'ve used all 3 free revisions — your vendor may request an extra fee.', { duration: 8000 });
        } else {
          toast.success(`Revision requested. ${data.revisions_remaining} free revision${data.revisions_remaining === 1 ? '' : 's'} remaining.`);
        }
        setRevisionText('');
        setShowRevisionForm(false);
        onUpdate?.();
      } else {
        toast.error(data.detail || 'Failed to request revision');
      }
    } catch {
      toast.error('Failed to request revision');
    } finally {
      setSendingRevision(false);
    }
  };

  const completeMilestone = async (step) => {
    const ms = milestones.find(m => m.step === step);
    if (ms?.photo_required && !photoFile) {
      toast.error(`Photo proof required for "${ms.label}"`);
      return;
    }
    setCompleting(step);
    try {
      let photo_base64 = null;
      if (photoFile) {
        const reader = new FileReader();
        photo_base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(photoFile);
        });
      }
      const res = await fetch(`${API}/milestones/complete/`, {
        method: 'POST', headers, body: JSON.stringify({ booking_id: bookingId, step, note, photo_base64 }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(`${ms?.label || step} completed!`); setNote(''); setPhotoFile(null); onUpdate?.(); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed to complete milestone'); }
    finally { setCompleting(null); }
  };

  const addTracking = async () => {
    if (!trackingInput.trim()) { toast.error('Tracking number required'); return; }
    try {
      const res = await fetch(`${API}/milestones/tracking/`, {
        method: 'POST', headers, body: JSON.stringify({ booking_id: bookingId, tracking_number: trackingInput, carrier: carrierInput }),
      });
      if (res.ok) { toast.success('Tracking number added!'); setShowTrackingForm(false); setTrackingInput(''); setCarrierInput(''); onUpdate?.(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="milestone-timeline">
      {/* ── Progress Bar (NEW) ── */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/10 to-[#3B82F6]/5 border border-[#8B5CF6]/15" data-testid="order-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-white font-syne font-bold text-sm">Order Progress</p>
            <p className="text-[#9CA3AF] text-xs mt-0.5" data-testid="progress-label">{computedLabel}</p>
          </div>
          <p className="text-[#A78BFA] font-mono font-bold text-2xl" data-testid="progress-percent">{computedPercent}%</p>
        </div>
        <div className="w-full h-2.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#8B5CF6] via-[#3B82F6] to-[#10B981] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${computedPercent}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <p className="text-[#64748B] text-[10px] font-mono">
            {milestones?.filter((m) => m.status === 'completed').length || 0} of {milestones?.length || 0} milestones complete
          </p>
          {!isVendor && (
            <p className="text-[#64748B] text-[10px]" data-testid="revisions-counter">
              Revisions used: <span className="text-[#A78BFA] font-mono font-bold">{revisionsUsed}/{revisionsLimit}</span>
            </p>
          )}
        </div>
      </div>

      {/* ── Revision Request (customer only, visible once order is completed) ── */}
      {!isVendor && orderCompleted && (
        <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="revision-panel">
          {!showRevisionForm ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-white font-syne font-bold text-sm">Not quite right?</p>
                <p className="text-[#9CA3AF] text-xs mt-0.5">
                  {remaining > 0
                    ? `You have ${remaining} free revision${remaining === 1 ? '' : 's'} remaining before extra charges may apply.`
                    : 'Your 3 free revisions are used up. Additional revisions may be subject to an extra charge from your vendor.'}
                </p>
              </div>
              <Button onClick={() => setShowRevisionForm(true)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-bold shrink-0" data-testid="request-revision-btn">
                Request Revision
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-white font-bold text-sm">Describe what you'd like revised</p>
              <Textarea
                value={revisionText}
                onChange={(e) => setRevisionText(e.target.value)}
                placeholder="e.g., Please change the color palette to warmer tones and remove the border on image 2."
                className="bg-white/5 border-white/10 text-white text-sm min-h-[80px]"
                data-testid="revision-text"
              />
              {remaining <= 0 && (
                <p className="text-[#FCD34D] text-[11px]">
                  ⚠️ You've used all 3 free revisions. Your vendor may respond with a custom offer for additional revisions.
                </p>
              )}
              <div className="flex gap-2">
                <Button onClick={requestRevision} disabled={sendingRevision} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-bold flex-1" data-testid="submit-revision-btn">
                  {sendingRevision ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                  Send Revision Request
                </Button>
                <Button onClick={() => { setShowRevisionForm(false); setRevisionText(''); }} variant="outline" className="border-white/10 text-[#9CA3AF] text-xs">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Milestone list ── */}
      <div className="space-y-0">
      {milestones.map((ms, idx) => {
        const Icon = stepIcons[ms.step] || Clock;
        const isDone = ms.status === 'completed';
        const isCurrent = ms.status === 'current';
        const isPending = ms.status === 'pending';

        return (
          <div key={ms.step} className="relative" data-testid={`milestone-${ms.step}`}>
            {/* Connector line */}
            {idx > 0 && <div className={`absolute left-5 -top-3 w-0.5 h-6 ${isDone ? 'bg-green-500' : 'bg-white/10'}`} />}

            <div className={`flex gap-4 p-4 rounded-xl transition-all ${isCurrent ? 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/20' : isDone ? 'bg-green-500/5' : 'opacity-50'}`}>
              {/* Status indicator */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDone ? 'bg-green-500 text-white' : isCurrent ? 'bg-[#8B5CF6] text-white animate-pulse' : 'bg-white/10 text-[#9CA3AF]'}`}>
                {isDone ? <Check size={18} /> : <Icon size={18} />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-syne font-bold text-sm ${isDone ? 'text-green-400' : isCurrent ? 'text-white' : 'text-[#9CA3AF]'}`}>{ms.label}</p>
                    <p className="text-[#64748B] text-xs mt-0.5">{ms.description}</p>
                  </div>
                  {ms.photo_required && <Camera size={14} className={`${isDone ? 'text-green-400' : 'text-[#8B5CF6]'}`} title="Photo required" />}
                </div>

                {/* Completed milestone details */}
                {isDone && (
                  <div className="mt-2 space-y-1">
                    {ms.completed_at && <p className="text-[#64748B] text-[10px]">{new Date(ms.completed_at).toLocaleString()}</p>}
                    {ms.note && <p className="text-[#9CA3AF] text-xs italic">"{ms.note}"</p>}
                    {ms.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${ms.photo_url}`} alt="" className="w-20 h-20 rounded-lg object-cover mt-1 border border-white/10" />}
                  </div>
                )}

                {/* Vendor: Complete this milestone */}
                {isCurrent && isVendor && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 space-y-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add progress notes..." className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]" />
                    {ms.photo_required && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-[#9CA3AF] hover:text-white hover:border-[#8B5CF6]/30 transition-all">
                          <Camera size={12} /> {photoFile ? photoFile.name : 'Upload Photo Proof *'}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files[0])} />
                      </div>
                    )}
                    <Button onClick={() => completeMilestone(ms.step)} disabled={completing === ms.step} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold text-sm w-full" data-testid={`complete-${ms.step}`}>
                      {completing === ms.step ? <Loader2 size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" />}
                      Mark as Complete
                    </Button>
                  </motion.div>
                )}

                {/* Tracking number for "shipped" step */}
                {ms.step === 'shipped' && isDone && trackingNumber && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <Truck size={12} className="text-blue-400" />
                    <span className="text-blue-400 text-xs font-mono font-bold">{trackingNumber}</span>
                  </div>
                )}
                {ms.step === 'shipped' && isCurrent && isVendor && !showTrackingForm && (
                  <button onClick={() => setShowTrackingForm(true)} className="mt-2 text-xs text-[#A78BFA] hover:text-white">+ Add tracking number</button>
                )}
                {showTrackingForm && ms.step === 'shipped' && isCurrent && (
                  <div className="mt-2 flex gap-2">
                    <Input value={trackingInput} onChange={(e) => setTrackingInput(e.target.value)} placeholder="Tracking #" className="bg-white/5 border-white/10 text-white text-xs flex-1" />
                    <Input value={carrierInput} onChange={(e) => setCarrierInput(e.target.value)} placeholder="Carrier" className="bg-white/5 border-white/10 text-white text-xs w-24" />
                    <Button onClick={addTracking} className="bg-blue-500 text-white text-xs px-3">Add</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ── Customer Confirmation Button ──
export function CustomerConfirmButton({ bookingId, vendorType, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const token = localStorage.getItem('token');

  const handleConfirm = async (type) => {
    if (!window.confirm('Are you sure? This will release payment to the vendor.')) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/milestones/customer-confirm/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, type }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); onConfirm?.(data); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); }
    finally { setConfirming(false); }
  };

  return (
    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center" data-testid="customer-confirm-section">
      <p className="text-green-400 font-bold mb-2">
        {vendorType === 'service' ? 'Did the vendor complete the service?' : 'Did you receive your order?'}
      </p>
      <div className="flex gap-3 justify-center">
        <Button onClick={() => handleConfirm(vendorType === 'service' ? 'service_done' : 'received')} disabled={confirming} className="bg-green-500 hover:bg-green-400 text-white font-bold" data-testid="confirm-delivery-btn">
          {confirming ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle size={14} className="mr-2" />}
          {vendorType === 'service' ? 'Service Completed' : 'Product Received'}
        </Button>
      </div>
    </div>
  );
}

// ── Post-Completion Review Form ──
export function ReviewForm({ bookingId, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('token');

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/milestones/review/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, rating, comment }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); onSubmit?.(data); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-xl p-5" data-testid="review-form">
      <h3 className="font-syne font-bold text-white mb-4">Leave a Review</h3>
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} onClick={() => setRating(s)} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} data-testid={`star-${s}`}>
            <Star size={28} className={`transition-all ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-[#64748B]'}`} />
          </button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience..." className="bg-white/5 border-white/10 text-white mb-3 min-h-[80px]" data-testid="review-comment" />
      <Button onClick={handleSubmit} disabled={submitting} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold w-full" data-testid="submit-review">
        {submitting ? 'Submitting...' : 'Submit Review'}
      </Button>
    </div>
  );
}
